import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
    SERVICE_AREAS_DATA,
    SHARED_PRODUCTS,
    SHARED_STEPS,
    SHARED_TESTIMONIALS,
    QuoteProduct,
    ServiceAreaData,
    StepItem,
    TestimonialItem
} from './service-area.data';

@Component({
    selector: 'app-service-area',
    templateUrl: './service-area.component.html',
    styleUrls: ['./service-area.component.scss']
})
export class ServiceAreaComponent implements OnInit, AfterViewInit, OnDestroy {
    currentArea: ServiceAreaData = SERVICE_AREAS_DATA['charlotte'];
    steps: StepItem[] = SHARED_STEPS;
    products: QuoteProduct[] = SHARED_PRODUCTS;
    testimonials: TestimonialItem[] = SHARED_TESTIMONIALS;

    private observer?: IntersectionObserver;
    private routeSub?: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private titleService: Title,
        private hostElement: ElementRef<HTMLElement>
    ) {}

    ngOnInit(): void {
        this.resolveArea();

        this.routeSub = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                this.resolveArea();
                if (typeof window !== 'undefined') {
                    window.scrollTo(0, 0);
                }
                setTimeout(() => {
                    this.initScrollReveal();
                }, 50);
            });
    }

    ngAfterViewInit(): void {
        this.initScrollReveal();
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.observer?.disconnect();
    }

    toggleFaq(index: number): void {
        if (!this.currentArea?.faqs || !this.currentArea.faqs[index]) {
            return;
        }
        this.currentArea.faqs[index].isOpen = !this.currentArea.faqs[index].isOpen;
    }

    private resolveArea(): void {
        let areaKey = 'charlotte';

        // Check route data first (e.g. data: { area: 'charlotte' })
        if (this.route.snapshot.data && this.route.snapshot.data['area']) {
            areaKey = this.route.snapshot.data['area'];
        } else if (this.route.snapshot.params && this.route.snapshot.params['area']) {
            areaKey = this.route.snapshot.params['area'];
        } else {
            // Check url path
            const path = this.router.url.split('?')[0].replace(/^\//, '');
            if (path.startsWith('service-area-')) {
                areaKey = path.replace('service-area-', '');
            } else if (path.startsWith('service-areas/')) {
                areaKey = path.replace('service-areas/', '');
            }
        }

        if (SERVICE_AREAS_DATA[areaKey]) {
            this.currentArea = JSON.parse(JSON.stringify(SERVICE_AREAS_DATA[areaKey]));
        } else {
            this.currentArea = JSON.parse(JSON.stringify(SERVICE_AREAS_DATA['charlotte']));
        }

        if (this.currentArea.pageTitle) {
            this.titleService.setTitle(this.currentArea.pageTitle);
        }
    }

    private initScrollReveal(): void {
        if (
            typeof window === 'undefined' ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return;
        }

        this.observer?.disconnect();

        const host = this.hostElement.nativeElement;
        const elements = Array.from(
            host.querySelectorAll<HTMLElement>('[data-reveal]')
        );

        if (!elements.length) {
            return;
        }

        elements.forEach((element) => {
            element.classList.remove('reveal-visible');
            element.classList.add('reveal-hidden');

            const delay = element.getAttribute('data-reveal-delay');
            if (delay) {
                const delayNumber = parseInt(delay, 10);
                if (!Number.isNaN(delayNumber)) {
                    element.style.setProperty(
                        '--reveal-delay',
                        `${delayNumber}ms`
                    );
                }
            }
        });

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    const element = entry.target as HTMLElement;
                    element.classList.remove('reveal-hidden');
                    element.classList.add('reveal-visible');

                    this.observer?.unobserve(element);
                });
            },
            {
                threshold: 0.12,
                rootMargin: '0px 0px -40px 0px'
            }
        );

        elements.forEach((element) => {
            this.observer?.observe(element);
        });
    }
}
